import React from "react";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";

const POST = POSTS.find((p) => p.slug === "why-spreadsheets-fail-camera-clubs");
const OG_IMAGE = "https://cameraclubscore.com/og/blog-why-spreadsheets-fail-camera-clubs.png";
const CANONICAL = "https://cameraclubscore.com/blog/why-spreadsheets-fail-camera-clubs";

// Warm, practical voice. Aimed at committee members who've been burned
// by their existing spreadsheet workflow. The tone is "I know how you
// feel" rather than "our features are amazing" — because trust matters
// more than pitch when you're asking someone to change their process.
export default function PostSpreadsheetsFail() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={POST.title} />
        <meta name="twitter:description" content={POST.excerpt} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <BlogPostShell post={POST}>
      <p className="lead">
        Every camera club has been there. It's judging night, the
        projector's warming up, and the person on scoring duty is
        squinting at a spreadsheet on a battered laptop. Somebody says
        "wait, wasn't Alice's second image a nine?" and the whole room
        pauses while three people flip through paper printouts to check.
      </p>

      <p>
        We built Camera Club Score because we watched this scene play
        out one too many times. Not because spreadsheets are bad — they
        aren't. They're brilliant tools that have carried camera clubs
        for twenty years. But somewhere between the seventh season and
        the third committee handover, a spreadsheet stops being a tool
        and starts being a liability.
      </p>

      <h2>The three things that quietly break</h2>

      <h3>1. Nobody remembers how it works except the last person</h3>
      <p>
        The club treasurer built a spreadsheet in 2014 with cell
        references and hidden columns. It's beautiful. It also has forty
        formulas whose logic lives entirely in her head. She's retiring
        this year. Everyone's smiling nervously.
      </p>
      <p>
        This is the single most common story we hear. The moment the
        person who <em>knows the spreadsheet</em> steps back, the club
        either freezes the format or starts a painful rewrite. Neither
        is fun on a Tuesday evening in the church hall.
      </p>

      <h3>2. Live editing on judging night is where mistakes hide</h3>
      <p>
        Judges talk fast. Whoever's on scoring duty is trying to type
        the score, remember the placement, and listen to the next
        image's title all at once. One typo — an 8 for an 18, a swap
        between two adjacent rows — and the whole season table shifts
        without anyone noticing until certificates go out.
      </p>
      <p>
        Spreadsheets don't help you here. They'll happily let you
        overwrite a cell with anything. There's no "undo the last
        mistake I didn't see myself make" button.
      </p>

      <h3>3. The season league is a black box</h3>
      <p>
        Ask most clubs to explain <em>exactly</em> how their season
        totals are calculated and you'll get a slightly different answer
        from every committee member. Some clubs count best five of
        seven. Some drop the lowest. Some award bonus points for
        placements. All of these rules live inside cell formulas that
        were correct in 2019 and may or may not still be right.
      </p>
      <p>
        When a photographer politely asks why they're fourth and not
        third, the answer shouldn't be "let me get back to you next
        week."
      </p>

      <h2>What changes when you use a purpose-built tool</h2>

      <p>
        We designed Camera Club Score around three ideas that seem
        obvious in hindsight but genuinely make a difference:
      </p>

      <p>
        <strong>Everything is auditable.</strong> Every score, every
        placement, every edit is captured. When a member asks why
        they're fourth, you can show them exactly which competition,
        which image, which score. No handwaving.
      </p>

      <p>
        <strong>The rules are the rules.</strong> Once you set your
        scoring mode (best five of seven, all scores, PoY points table),
        the totals recalculate automatically as scores come in. There's
        no formula to break because there's no formula to write.
      </p>

      <p>
        <strong>Nothing is trapped.</strong> Every screen has an export
        button. CSV for spreadsheet lovers, PDF for print, a public
        portal link for the club website. Your data stays yours — you
        can walk away with all of it at any time.
      </p>

      <h2>The part we didn't expect to matter</h2>
      <p>
        The single feature that gets the most enthusiastic feedback isn't
        the fastest scoring grid or the tidiest league table. It's the
        <strong> read-only public portal</strong>. Every club gets a
        shareable link that shows the current season standings — no
        login required, no admin fuss.
      </p>
      <p>
        Club members love it because they can check where they are on
        their phone during the week. Committees love it because it
        means fewer emails asking "am I still winning the mono league?"
        And when someone new turns up to a meeting and asks what the
        club's about, you can just show them the portal on your phone.
      </p>

      <h2>What we're <em>not</em> trying to do</h2>
      <p>
        Camera Club Score isn't trying to replace your website. It isn't
        a photo-hosting service. It doesn't try to be a members' forum
        or an image gallery. Every one of those tools already exists and
        does its job well.
      </p>
      <p>
        We do one thing: run competition scoring end-to-end, from
        roster to results, with as little friction as possible. That's
        it. And we think doing one thing well is worth more than doing
        everything moderately.
      </p>

      <h2>If your club is still on a spreadsheet</h2>
      <p>
        That's totally fine — spreadsheets have served camera clubs
        beautifully for decades. But if you're the one who ends up
        rebuilding it every autumn, or the one panicking with a broken
        formula ten minutes before a judging night, we'd love to save
        you the trouble.
      </p>
      <p>
        Camera Club Score is free for camera clubs to use. Sign up in
        thirty seconds, import your existing photographers list, and
        run your next competition without touching a spreadsheet.
        We think you'll be surprised how much quieter judging night gets.
      </p>
    </BlogPostShell>
    </>
  );
}
